import { add, startOfHour } from 'date-fns';
import {
  DataSet,
  MultiMatchStringFilterRequest,
  RequestType,
  SearchAggregation,
  SearchDataRequestType,
  SearchRequest,
  SortField,
  StringListFilterRequest,
  StringValueFilterRequest,
} from 'src/types/Search';
import { SortByType } from './types';

export interface ContractTransformer {
  startDate?: number;
  endDate?: number;
  username?: string,
  tagIdList?: Array<string>;
  categoryIdList?: Array<string>;
  groupIdList?: Array<string>;
  sortBy?: SortByType;
  lastEvaluatedKeys?: Array<string>;
  dataSetType?: DataSet;
  searchTerm?: string;
  hasReplies?: boolean;
  hasSolution?: boolean;
  amazonReplied?: boolean;
  hasPinned?: boolean;
  pinnedToCategory?: boolean;
  pinnedToGroup?: boolean;
  pinnedToListing?: boolean;
  userId?: string;
  fetchAggregations?: boolean;
  pageSize?: number;
  excludeThread?: string;
  requestType?: RequestType;
  groupId?: string;
  searchDataRequestType?: SearchDataRequestType;
}

export const createSearchAPIContract = ({
  startDate,
  endDate,
  username,
  tagIdList,
  categoryIdList,
  groupIdList,
  sortBy,
  lastEvaluatedKeys,
  dataSetType = DataSet.content,
  searchTerm = '',
  hasReplies,
  hasSolution,
  amazonReplied,
  pinnedToCategory,
  pinnedToGroup,
  pinnedToListing,
  userId,
  fetchAggregations = false,
  pageSize = 10,
  excludeThread,
  requestType = RequestType.search,
  groupId,
  searchDataRequestType,
}: ContractTransformer): SearchRequest => {
  const getFilterFields = () => {
    const fields = [
      ...(tagIdList && tagIdList.length > 0
        ? [
          {
            __type:
              'StringListFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'tags',
            operator: 'AND',
            comparator: 'CONTAINS',
            values: tagIdList,
          } as StringListFilterRequest,
        ]
        : []),
      ...(categoryIdList && categoryIdList.length > 0
        ? [
          {
            __type:
                'StringListFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'categoryId',
            operator: 'AND',
            comparator: 'CONTAINS',
            values: categoryIdList,
          } as StringListFilterRequest,
        ]
        : []),
      ...((groupIdList && groupIdList.length > 0)
        ? [
          {
            __type:
                'StringListFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'groupId',
            operator: 'AND',
            comparator: 'CONTAINS',
            values: groupIdList,
          } as StringListFilterRequest,
        ]
        : []),
      ...(hasReplies !== undefined
        ? [
          {
            __type:
                'StringValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'replied',
            operator: 'AND',
            comparator: 'CONTAINS',
            value: hasReplies.toString(),
          } as StringValueFilterRequest,
          {
            __type:
                'StringValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'postType',
            operator: 'AND',
            comparator: 'CONTAINS',
            value: 'OP',
          } as StringValueFilterRequest,
        ]
        : []),
      ...(userId !== undefined
        ? [
          {
            __type:
                'StringValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'userId',
            operator: 'AND',
            comparator: 'CONTAINS',
            value: userId,
          } as StringValueFilterRequest,
        ]
        : []),
      ...(amazonReplied !== undefined
        ? [
          {
            __type:
                'StringValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'hasAmazonReply',
            operator: 'AND',
            comparator: 'CONTAINS',
            value: amazonReplied.toString(),
          } as StringValueFilterRequest,
        ]
        : []),
      ...(hasSolution !== undefined
        ? [
          {
            __type:
                'StringValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'hasSolution',
            operator: 'AND',
            comparator: 'CONTAINS',
            value: hasSolution.toString(),
          } as StringValueFilterRequest,
        ]
        : []),
      ...(pinnedToCategory !== undefined
        ? [
          {
            __type:
                    'BooleanValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'pinnedTo.category',
            operator: 'AND',
            comparator: 'EQ',
            value: pinnedToCategory.toString(),
          } as StringValueFilterRequest,
        ]
        : []),
      ...(pinnedToGroup !== undefined
        ? [
          {
            __type:
                      'BooleanValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'pinnedTo.group',
            operator: 'AND',
            comparator: 'EQ',
            value: pinnedToGroup.toString(),
          } as StringValueFilterRequest,
        ]
        : []),
      ...(pinnedToListing !== undefined
        ? [
          {
            __type:
                  'BooleanValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'pinnedTo.listing',
            operator: 'AND',
            comparator: 'EQ',
            value: pinnedToListing.toString(),
          } as StringValueFilterRequest,
        ]
        : []),
      ...(excludeThread !== undefined
        ? [
          {
            __type:
                'StringValueFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
            key: 'threadId',
            operator: 'AND',
            comparator: 'CONTAINS',
            value: excludeThread,
            negate: 'true',
          } as StringValueFilterRequest,
        ]
        : []),
    ];
    if (fields.length === 0) return undefined;
    return fields;
  };

  const getSearchFields = () => {
    const fields = [];

    if (searchTerm) {
      fields.push({
        __type: 'MultiMatchStringFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
        keys: ['content', 'title'],
        operator: 'AND',
        comparator: 'CONTAINS',
        value: searchTerm,
      } as MultiMatchStringFilterRequest);
    }
    if (username && username.length > 0) {
      fields.push({
        __type: 'PrefixFilterRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
        key: 'username',
        operator: 'AND',
        comparator: 'CONTAINS',
        value: username,
      } as StringValueFilterRequest);
    }

    if (fields.length === 0) return undefined;
    return fields;
  };

  const requestParamsKey = requestType === RequestType.search
    ? 'searchParamsPerDataSetList'
    : 'listingCriteriaParamsList';

  return {
    __type:
      requestType === RequestType.search
        ? 'SearchDataRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/'
        : 'GetListingForUserRequest:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
    pageSize,
    marketplaceId: '123',
    context: {
      ...(searchDataRequestType && { requestType: searchDataRequestType }),
      ...(groupId && { groupId }),
    },
    lastEvaluatedKeys,
    [requestParamsKey]: [
      {
        dataset: dataSetType,
        ...(!!startDate
          && !!endDate && {
          dateRangeFilters: [
            {
              fieldName: 'createdAt',
              dateRange: {
                /**
                 * Convert from milliseconds to seconds
                 * endOfHour gives the last millisecond within that hour which
                 * backend is not in favour of using startOfHour instead for endDate
                 */
                startDate: startOfHour(startDate).getTime() / 1000,
                endDate: startOfHour(add(endDate, { hours: 1 })).getTime() / 1000,
              },
            },
          ],
        }),
        filterFields: getFilterFields(),
        searchFields: getSearchFields(),
        ...(fetchAggregations && {
          searchAggregations: [
            {
              __type:
                'SearchTermAggregation:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
              aggregationName: 'tags',
              fieldName: 'tags',
            } as SearchAggregation,
            {
              __type:
                'SearchTermAggregation:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
              aggregationName: 'categories',
              fieldName: 'categoryId',
            } as SearchAggregation,
            {
              __type:
                'SearchTermAggregation:http://internal.amazon.com/coral/com.amazon.fresnoservice/',
              aggregationName: 'amazonRepliedAggs',
              fieldName: 'hasAmazonReply',
            } as SearchAggregation,
          ],
        }),
        sortFields: [
          ...(sortBy
            ? [
              {
                fieldName: sortBy,
                sortAscending: false,
              },
            ]
            : []),
        ] as Array<SortField>,
      },
    ],
  };
};
